'use client'
import { type CSSProperties, Fragment, type ReactNode } from 'react'

import { Box, Container, Heading, Text } from '@doist/marketist'

import { useMatchMedia } from 'services/media-matcher'

import css from './main-page-header.module.css'

type MainPageHeaderProps = {
    title: string | ReactNode
    subtitle?: string | ReactNode
    buttonRow?: ReactNode[]
    buttonBottom?: ReactNode
    mobileButtonRow?: ReactNode[]
    mobileButtonBottom?: ReactNode
    size?: 'sm' | 'base'
    sizeMobile?: 'sm' | 'base'
    style?: CSSProperties
    padded?: boolean
}

function MainPageHeader({
    title,
    subtitle,
    size = 'base',
    style = {},
    sizeMobile,
    padded = true,
    buttonRow: br,
    buttonBottom: bb,
    mobileButtonRow: mbr,
    mobileButtonBottom: mbb,
}: MainPageHeaderProps) {
    const isMobile = useMatchMedia('(max-width: 768px)')
    const currentSize = isMobile && sizeMobile ? sizeMobile : size
    const headingSize = currentSize === 'sm' ? 'major-lg' : 'major-2xl'
    const subtitleSize = currentSize === 'sm' ? 'body-base' : 'body-lg'
    const containerGap = currentSize === 'sm' ? 'var(--space-24)' : 'var(--space-32)'
    const textGap = currentSize === 'sm' ? 'var(--space-8)' : 'var(--space-24)'
    const buttonRow = isMobile && mbr && mbr.length > 0 ? mbr : br
    const buttonBottom = isMobile && mbb ? mbb : bb

    return (
        <Container
            tag="header"
            className={css.container}
            style={{ '--text-gap': textGap, '--container-gap': containerGap, ...style }}
            padded={padded}
        >
            <Box className={css.textWrapper}>
                <Heading size={headingSize} level="1">
                    {title}
                </Heading>
                {subtitle && (
                    <Text weight="medium" size={subtitleSize}>
                        {subtitle}
                    </Text>
                )}
            </Box>
            {buttonRow ? (
                <Box className={css.buttonWrapper}>
                    <Box className={css.buttonRow}>
                        {buttonRow.map((button, index) => (
                            <Fragment key={`btn-${index}`}>{button}</Fragment>
                        ))}
                    </Box>
                    {buttonBottom ? <>{buttonBottom}</> : null}
                </Box>
            ) : null}
        </Container>
    )
}

export { MainPageHeader }
